/* import __COLOCATED_TEMPLATE__ from './branding-and-colors.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { debounce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { DEFAULT_MESSENGER_COLORS } from 'embercom/lib/messenger-settings-defaults';
import type MessengerSettingsThemeProvider from 'embercom/services/messenger-settings-theme-provider';
import type IntlService from 'ember-intl/services/intl';

export interface Args {
  lookAndFeelSettings?: $TSFixMe;
  activeBrand?: $TSFixMe;
  selectedLayout?: string;
  isMobilePlatform?: boolean;
  configuration?: $TSFixMe;
  app?: $TSFixMe;
  isDarkModeEnabled?: boolean;
  hasHub?: boolean;
}

const THEME_PROPERTIES = {
  backgroundType: {
    light: 'backgroundType',
    dark: 'backgroundTypeDark',
  },
  actionColor: {
    light: 'messengerActionColor',
    dark: 'messengerActionColorDark',
  },
  actionColorHex: {
    light: 'messengerActionColorHex',
    dark: 'messengerActionColorDarkHex',
  },
  backgroundColor: {
    light: 'messengerBackgroundColor',
    dark: 'messengerBackgroundColorDark',
  },
  backgroundColorHex: {
    light: 'messengerBackgroundColorHex',
    dark: 'messengerBackgroundColorDarkHex',
  },
  textColor: {
    light: 'textColor',
    dark: 'textColorTypeDark',
  },
  fadeToBackground: {
    light: 'fadeToWhite',
    dark: 'fadeToDark',
  },
};

const LOOK_AND_FEEL_PROPERTIES = {
  primary: {
    light: 'primaryColor',
    dark: 'primaryColorDark',
  },
  secondary: {
    light: 'secondaryColor',
    dark: 'secondaryColorDark',
  },
};

const ANALYTICS_OBJECTS = {
  actionColor: {
    light: 'action_color',
    dark: 'action_color_dark',
  },
  backgroundColor: {
    light: 'background_color',
    dark: 'background_color_dark',
  },
};

export default class BrandingAndColors extends Component<Args> {
  @service declare router: any;
  @service declare appService: any;
  @service declare MessengerSettingsService: $TSFixMe;
  @service declare messengerSettingsThemeProvider: MessengerSettingsThemeProvider;
  @service declare intl: IntlService;

  colorOptions = DEFAULT_MESSENGER_COLORS;

  get app() {
    return this.appService.app;
  }

  get lookAndFeelSettings() {
    return this.args.lookAndFeelSettings;
  }

  get activeBrand() {
    return this.args.activeBrand;
  }

  get compactEnabled() {
    return this.args.selectedLayout === 'compact' && !this.args.isMobilePlatform;
  }

  get selectedTheme() {
    return this.messengerSettingsThemeProvider.targetThemeType;
  }

  get isDarkTheme() {
    return this.messengerSettingsThemeProvider.isDarkTheme;
  }

  get isSystemTheme() {
    return this.activeBrand.themeMode === 'system';
  }

  private getThemePropertyName(propertyMap: { light: string; dark: string }) {
    return this.isDarkTheme ? propertyMap.dark : propertyMap.light;
  }

  private getThemePropertyValue(propertyKey: keyof typeof THEME_PROPERTIES) {
    let propertyMap = THEME_PROPERTIES[propertyKey];
    let propertyName = this.getThemePropertyName(propertyMap);
    let value = this.activeBrand?.[propertyName];
    return value;
  }

  private setThemePropertyValue(propertyKey: keyof typeof THEME_PROPERTIES, value: any) {
    let propertyMap = THEME_PROPERTIES[propertyKey];
    let propertyName = this.getThemePropertyName(propertyMap);

    if (this.activeBrand) {
      this.activeBrand[propertyName] = value;
    }
  }

  get selectedBackgroundType() {
    return this.getThemePropertyValue('backgroundType');
  }

  set selectedBackgroundType(value: string) {
    this.setBackgroundType(value);
  }

  get selectedActionColor() {
    return this.getThemePropertyValue('actionColor');
  }

  get selectedActionColorHex() {
    return this.getThemePropertyValue('actionColorHex');
  }

  get selectedBackgroundColor() {
    return this.getThemePropertyValue('backgroundColor');
  }

  get selectedBackgroundColorHex() {
    return this.getThemePropertyValue('backgroundColorHex');
  }

  get selectedTextColor() {
    return this.getThemePropertyValue('textColor');
  }

  set selectedTextColor(value: string) {
    this.setTextColor(value);
  }

  get fadeToBackground() {
    return this.getThemePropertyValue('fadeToBackground');
  }

  @action
  setBackgroundType(value: string) {
    this.setThemePropertyValue('backgroundType', value);
  }

  @action
  setPrimaryColor(color: string) {
    let colorPropertyName = this.getThemePropertyName(THEME_PROPERTIES.actionColor);
    let lookAndFeelPropertyName = this.getThemePropertyName(LOOK_AND_FEEL_PROPERTIES.primary);
    let analyticsObject = this.getThemePropertyName(ANALYTICS_OBJECTS.actionColor);

    this.args.activeBrand[colorPropertyName] = color;

    if (this.args.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[lookAndFeelPropertyName] = color;
    }

    this.debouncedTrackColorChange(analyticsObject, color);
  }

  @action
  setSecondaryColor(color: string) {
    let colorPropertyName = this.getThemePropertyName(THEME_PROPERTIES.backgroundColor);
    let lookAndFeelPropertyName = this.getThemePropertyName(LOOK_AND_FEEL_PROPERTIES.secondary);
    let analyticsObject = this.getThemePropertyName(ANALYTICS_OBJECTS.backgroundColor);

    this.args.activeBrand[colorPropertyName] = color;

    if (this.args.activeBrand.isDefault) {
      this.args.lookAndFeelSettings[lookAndFeelPropertyName] = color;
    }

    this.debouncedTrackColorChange(analyticsObject, color);
  }

  @action
  setTextColor(value: string) {
    this.setThemePropertyValue('textColor', value);
  }

  @action
  toggleFadeToBackground() {
    let fadePropertyName = this.getThemePropertyName(THEME_PROPERTIES.fadeToBackground);
    this.activeBrand[fadePropertyName] = !this.fadeToBackground;
  }

  debouncedTrackColorChange(object: $TSFixMe, color: string) {
    debounce(
      this.MessengerSettingsService,
      this.MessengerSettingsService.trackAnalyticsEvent,
      {
        action: 'changed',
        object,
        color,
      },
      500,
    );
  }

  get hasActionColorAccessibilityIssue(): boolean {
    // We use the primary_type to determine if the action color is accessible in the current theme
    let primaryColorType = this.args.configuration?.appColors?.primary_type;
    let currentMessengerTheme = this.isDarkTheme ? 'dark' : 'light';
    return primaryColorType === currentMessengerTheme;
  }

  get actionColorAccessibilityWarningMessage(): string {
    let fallbackColorKey = this.isDarkTheme ? 'light' : 'dark';
    let fallbackColorName = this.intl
      .t(`messenger.styling.header-text-color.options.${fallbackColorKey}.name`)
      .toLowerCase();

    return this.intl.t('messenger.styling.action.accessibility-warning', {
      fallbackColorName,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Styling::BrandingAndColors': typeof BrandingAndColors;
    'messenger-settings/styling/branding-and-colors': typeof BrandingAndColors;
    'MessengerSettings::LookAndFeel::LogoUploadComponent': any;
    'MessengerSettings::Styling::Background::ColorPickers': any;
    'MessengerSettings::Styling::Background::ImageUpload': any;
  }
}
